import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API } from '../contexts/AuthContext';

export default function Perfis() {
  const [perfis, setPerfis] = useState([]);
  const [permissoes, setPermissoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [form, setForm] = useState({ id: null, nome: '', descricao: '', permissoes: [] });
  const [salvando, setSalvando] = useState(false);

  const carregar = () => {
    setLoading(true);
    setErro('');
    Promise.all([
      axios.get(`${API}/perfis`),
      axios.get(`${API}/perfis/permissoes`),
    ])
      .then(([p, perm]) => {
        setPerfis(p.data?.data || []);
        setPermissoes(perm.data?.data || []);
      })
      .catch(() => setErro('Não foi possivel carregar os perfis.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { carregar(); }, []);

  const limparForm = () => {
    setForm({ id: null, nome: '', descricao: '', permissoes: [] });
  };

  const editar = (perfil) => {
    setForm({
      id: perfil.id,
      nome: perfil.nome,
      descricao: perfil.descricao || '',
      permissoes: perfil.permissoes || [],
    });
  };

  const togglePermissao = (chave) => {
    setForm((f) => ({
      ...f,
      permissoes: f.permissoes.includes(chave)
        ? f.permissoes.filter((p) => p !== chave)
        : [...f.permissoes, chave],
    }));
  };

  const grupos = useMemo(() => {
    const map = {};
    permissoes.forEach((p) => {
      const g = p.grupo || 'Outros';
      if (!map[g]) map[g] = [];
      map[g].push(p);
    });
    return map;
  }, [permissoes]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.nome) {
      Swal.fire({ icon: 'warning', title: 'Nome e obrigatório.' });
      return;
    }
    setSalvando(true);
    try {
      const payload = {
        nome: form.nome,
        descricao: form.descricao,
        permissoes: form.permissoes,
      };
      if (form.id) {
        await axios.put(`${API}/perfis/${form.id}`, payload);
      } else {
        await axios.post(`${API}/perfis`, payload);
      }
      Swal.fire({ icon: 'success', title: form.id ? 'Perfil atualizado' : 'Perfil criado', timer: 1500, showConfirmButton: false });
      limparForm();
      carregar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erro', text: err.response?.data?.message || 'Erro ao salvar perfil.' });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">Administração</span>
          <h1>Perfis e permissões</h1>
          <p>Defina perfis de acesso e quais areas do portal cada um pode usar.</p>
        </div>
      </section>

      {erro ? <div className="feedback-banner feedback-banner--error">{erro}</div> : null}

      <section className="content-card">
        <div className="section-header section-header--compact">
          <div>
            <span className="section-header__eyebrow">{form.id ? 'Editar' : 'Novo'}</span>
            <h2>{form.id ? `Editar ${form.nome || 'perfil'}` : 'Novo perfil'}</h2>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="portal-form portal-form--compact">
          <div className="portal-form-grid portal-form-grid--perfis">
            <label className="portal-field">
              <span>Nome *</span>
              <input value={form.nome} onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))} required />
            </label>
            <label className="portal-field">
              <span>Descrição</span>
              <input value={form.descricao} onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))} />
            </label>
          </div>

          <div className="permissoes-box">
            {Object.entries(grupos).map(([grupo, perms]) => (
              <div key={grupo} className="permissoes-grupo">
                <div className="permissoes-grupo__titulo">{grupo}</div>
                <div className="permissoes-chips">
                  {perms.map((perm) => {
                    const ativo = form.permissoes.includes(perm.chave);
                    return (
                      <label key={perm.chave} className={`permissao-chip${ativo ? ' permissao-chip--on' : ''}`}>
                        <input type="checkbox" checked={ativo} onChange={() => togglePermissao(perm.chave)} />
                        <span>{perm.nome}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="portal-form-actions">
            <button type="submit" className="btn-portal btn-portal--primary" disabled={salvando}>
              <i className={`bi ${form.id ? 'bi-check-lg' : 'bi-plus-circle'}`} />
              {salvando ? ' Salvando...' : form.id ? ' Atualizar perfil' : ' Criar perfil'}
            </button>
            {form.id && (
              <button type="button" className="btn-portal btn-portal--outline" onClick={limparForm}>
                Cancelar
              </button>
            )}
          </div>
        </form>
      </section>

      <section className="info-grid">
        {loading ? (
          <div className="content-card content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando perfis...</p>
          </div>
        ) : perfis.length ? perfis.map((p) => (
          <article key={p.id} className="info-card">
            <header>
              <span className="info-card__icon"><i className="bi bi-shield-lock" /></span>
              <div style={{ flex: 1 }}>
                <h3>Perfil</h3>
                <p>{p.nome}</p>
              </div>
              <button type="button" className="icon-action" title="Editar" onClick={() => editar(p)}>
                <i className="bi bi-pencil" />
              </button>
            </header>
            {p.descricao && <p style={{ color: 'var(--portal-muted)', fontSize: '0.85rem', margin: '0 0 10px' }}>{p.descricao}</p>}
            <div className="permissoes-chips permissoes-chips--readonly">
              {(p.permissoes || []).map((chave) => (
                <span key={chave} className="permissao-chip permissao-chip--on">
                  <span>{chave}</span>
                </span>
              ))}
              {(!p.permissoes || p.permissoes.length === 0) && (
                <span style={{ color: 'var(--portal-muted)', fontSize: '0.82rem' }}>Sem permissões</span>
              )}
            </div>
          </article>
        )) : (
          <div className="empty-state">
            <strong>Nenhum perfil cadastrado</strong>
            <span>Crie um perfil acima para comecar.</span>
          </div>
        )}
      </section>
    </div>
  );
}
